import { useState, useEffect, useCallback } from "react";
import { useRouter } from "next/router";

import {
  login,
  logout,
  registerUser,
  registerUserWithGoogle,
  registerUserWithFacebook,
  observer,
  sendResetPasswordEmail,
} from "api/auth.api";

const useAuth = () => {
  const router = useRouter();
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const unsubscribe = observer((user) => {
      if (user) {
        setCurrentUser(user);
        setIsLoading(false);
      } else {
        setCurrentUser(null);
        setIsLoading(false);
      }
    });
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  const signOut = useCallback(async () => {
    return logout().then(() =>
      router.push("/auth?mode=login", { replace: true }),
    );
  }, [router]);

  const loginWithEmailAndPassword = useCallback(
    async (email, password) => {
      return login(email, password).then(() => router.push("/"));
    },
    [router],
  );

  const register = useCallback(
    async (email, password) => {
      return registerUser(email, password).then(() => router.push("/"));
    },
    [router],
  );

  const loginWithGoogle = useCallback(async () => {
    return registerUserWithGoogle().then(() => router.push("/"));
  }, [router]);

  const loginWithFacebook = useCallback(async () => {
    return registerUserWithFacebook().then(() => router.push("/"));
  }, [router]);

  const sendPasswordResetEmail = useCallback(
    async (email) => {
      return sendResetPasswordEmail(email).then(() => router.push("/"));
    },
    [router],
  );

  return {
    currentUser,
    isLoading,
    loginWithEmailAndPassword,
    loginWithGoogle,
    loginWithFacebook,
    register,
    signOut,
    sendPasswordResetEmail,
  };
};

export default useAuth;
